// Lightweight, app-wide toast system.
//
// A singleton pub/sub (mirrors loadingManager) so ANY module — React components,
// services, or axios interceptors — can fire an on-brand toast without a hook:
//
//   import toast from "../../Utils/toastManager";
//   toast.success("Saved successfully");
//   toast.error(response?.error || "Something went wrong");
//   toast.warning("Please complete all required fields");
//
// The <Toaster /> mounted once in App.js subscribes to this manager and renders
// the stack. This replaces the native window.alert() used across the app.

const MAX_VISIBLE = 4;
const DEFAULT_DURATION = 4000;
const ERROR_DURATION = 5000;

let toasts = [];
let listeners = [];
let counter = 0;

const emit = () => {
  listeners.forEach((listener) => listener(toasts));
};

const dismiss = (id) => {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
};

const push = (type, message, options = {}) => {
  // Accept push(type, "message", { title, duration })
  //     or push(type, { title, message, duration }).
  const config =
    message && typeof message === "object" ? message : { ...options, message };

  // Gracefully coerce non-string messages (e.g. Error objects) to text.
  let text = config.message;
  if (text == null) text = "";
  else if (typeof text !== "string") text = text.message || String(text);

  const id = ++counter;
  const duration =
    config.duration != null
      ? config.duration
      : type === "error"
      ? ERROR_DURATION
      : DEFAULT_DURATION;

  const toast = { id, type, title: config.title || "", message: text, duration };

  // Keep only the most recent few so the stack never grows unbounded.
  toasts = [...toasts, toast].slice(-MAX_VISIBLE);
  emit();

  if (duration > 0) {
    setTimeout(() => dismiss(id), duration);
  }
  return id;
};

export const toastManager = {
  subscribe(listener) {
    listeners.push(listener);
    // Push current state immediately so a freshly mounted Toaster is in sync.
    listener(toasts);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  },
  dismiss,
  clear() {
    toasts = [];
    emit();
  },
};

const toast = {
  success: (message, options) => push("success", message, options),
  error: (message, options) => push("error", message, options),
  warning: (message, options) => push("warning", message, options),
  info: (message, options) => push("info", message, options),
  show: (message, options) => push((options && options.type) || "info", message, options),
  dismiss,
};

export default toast;
